import { oklch, formatHex, clampChroma } from 'culori'

// Generates a cohesive help-center palette from a single brand seed colour.
// Mirrors the server presenter's token model: outer canvas (bg) < fixed-width
// page band (page) < card surface (surface), plus border/text/muted.
//
// Built in OKLCH (perceptually uniform), so equal lightness steps look equal
// across every hue, and chroma is clamped to the source colour's own chroma —
// an achromatic seed (grey/black/white) has chroma 0, so it produces a neutral
// ladder instead of inventing a tint from a meaningless hue.

export interface HelpPalette {
  appearance: 'light' | 'dark'
  bg_color: string
  bg_accent_color: string
  page_color: string
  surface_color: string
  surface_hover_color: string
  border_color: string
  text_color: string
  muted_color: string
}

export const TREATMENTS = [
  { key: 'aurora-dark', label: 'Aurora (dark, tinted)' },
  { key: 'graphite-dark', label: 'Graphite (dark, neutral)' },
  { key: 'mist-light', label: 'Mist (light, tinted)' },
  { key: 'paper-light', label: 'Paper (light, neutral)' },
] as const

export type Treatment = (typeof TREATMENTS)[number]['key']

// [lightness 0..1, chroma target]. Chroma is later clamped to the source
// colour's chroma and reduced to fit sRGB gamut.
interface Step { l: number; c: number }
interface TreatmentSpec {
  appearance: 'light' | 'dark'
  // primary-tinted layers
  bg: Step; bgAccent: Step; page: Step; surface: Step; text: Step; muted: Step
  // accent-tinted layers
  hover: Step; border: Step
}

const SPECS: Record<Treatment, TreatmentSpec> = {
  'aurora-dark': {
    appearance: 'dark',
    bg: { l: 0.17, c: 0.04 }, bgAccent: { l: 0.2, c: 0.045 }, page: { l: 0.22, c: 0.05 },
    surface: { l: 0.27, c: 0.05 }, text: { l: 0.96, c: 0.02 }, muted: { l: 0.7, c: 0.03 },
    hover: { l: 0.33, c: 0.07 }, border: { l: 0.42, c: 0.08 },
  },
  'graphite-dark': {
    appearance: 'dark',
    bg: { l: 0.18, c: 0.008 }, bgAccent: { l: 0.21, c: 0.008 }, page: { l: 0.23, c: 0.008 },
    surface: { l: 0.28, c: 0.008 }, text: { l: 0.95, c: 0.005 }, muted: { l: 0.69, c: 0.008 },
    hover: { l: 0.33, c: 0.03 }, border: { l: 0.42, c: 0.035 },
  },
  'mist-light': {
    appearance: 'light',
    bg: { l: 0.96, c: 0.03 }, bgAccent: { l: 0.93, c: 0.035 }, page: { l: 0.99, c: 0.015 },
    surface: { l: 1, c: 0 }, text: { l: 0.27, c: 0.04 }, muted: { l: 0.56, c: 0.03 },
    hover: { l: 0.97, c: 0.05 }, border: { l: 0.88, c: 0.06 },
  },
  'paper-light': {
    appearance: 'light',
    bg: { l: 0.96, c: 0.006 }, bgAccent: { l: 0.93, c: 0.006 }, page: { l: 0.99, c: 0.004 },
    surface: { l: 1, c: 0 }, text: { l: 0.28, c: 0.01 }, muted: { l: 0.56, c: 0.008 },
    hover: { l: 0.97, c: 0.025 }, border: { l: 0.88, c: 0.03 },
  },
}

// Build a hex from a lightness/chroma step, tinted by `hue`, with chroma never
// exceeding the source colour's chroma, then reduced to fit the sRGB gamut.
function step(s: Step, hue: number | undefined, sourceChroma: number): string {
  const c = Math.min(s.c, sourceChroma)
  const fitted = clampChroma({ mode: 'oklch', l: s.l, c, h: hue ?? 0 }, 'oklch')
  return formatHex(fitted) ?? '#000000'
}

export function generatePalette(seed: string, accent: string, treatment: Treatment): HelpPalette {
  const p = oklch(seed) ?? { h: 0, c: 0 }
  const a = oklch(accent || seed) ?? p
  const pc = p.c ?? 0
  const ac = a.c ?? 0
  const spec = SPECS[treatment] ?? SPECS['aurora-dark']

  return {
    appearance: spec.appearance,
    bg_color: step(spec.bg, p.h, pc),
    bg_accent_color: step(spec.bgAccent, p.h, pc),
    page_color: step(spec.page, p.h, pc),
    surface_color: step(spec.surface, p.h, pc),
    surface_hover_color: step(spec.hover, a.h, ac),
    border_color: step(spec.border, a.h, ac),
    text_color: step(spec.text, p.h, pc),
    muted_color: step(spec.muted, p.h, pc),
  }
}
